import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Users,
  FileText,
  IndianRupee,
  Eye,
  Settings,
  Search,
  Plus,
  Check,
  X,
  Clock,
  AlertCircle,
  Building2,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Filter,
  MoreVertical,
  Download,
  FileSignature,
  Save,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BusinessPartnerManager = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('partners');
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Services Master (for partner service assignment)
  const [servicesMaster, setServicesMaster] = useState([]);
  
  // Document Config State
  const [documentConfigs, setDocumentConfigs] = useState([]);
  const [showDocConfigModal, setShowDocConfigModal] = useState(false);
  const [editingDocConfig, setEditingDocConfig] = useState(null);
  
  // Commission Config State
  const [commissionConfigs, setCommissionConfigs] = useState([]);
  const [showCommConfigModal, setShowCommConfigModal] = useState(false);
  const [editingCommConfig, setEditingCommConfig] = useState(null);
  
  // Stage Visibility State
  const [stageVisibility, setStageVisibility] = useState([]);
  
  // Settings State
  const [bpSettings, setBpSettings] = useState(null);
  
  // Partner Detail Modal
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  
  // Commission Records
  const [commissions, setCommissions] = useState([]);
  const [commissionFilter, setCommissionFilter] = useState('all');

  // Partner Agreement State
  const [agreement, setAgreement] = useState({
    title: 'Business Partner Agreement',
    content: '',
    version: '1.0'
  });
  const [agreementList, setAgreementList] = useState([]);
  const [agreementAcceptances, setAgreementAcceptances] = useState([]);
  const [savingAgreement, setSavingAgreement] = useState(false);

  // Fetch services master on mount
  useEffect(() => {
    fetchServicesMaster();
  }, []);

  const fetchServicesMaster = async () => {
    try {
      const response = await axios.get(`${API}/services`, { headers: getAuthHeader() });
      setServicesMaster(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'partners':
          await Promise.all([fetchPartners(), fetchStats()]);
          break;
        case 'documents':
          await fetchDocumentConfigs();
          break;
        case 'commissions':
          await fetchCommissionConfigs();
          break;
        case 'commission-records':
          await fetchCommissionRecords();
          break;
        case 'stages':
          await fetchStageVisibility();
          break;
        case 'settings':
          await fetchSettings();
          break;
        case 'agreement':
          await Promise.all([fetchAgreement(), fetchAgreementList(), fetchAgreementAcceptances()]);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${API}/business-partners/agreement`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setAgreement({
          title: response.data.title || 'Business Partner Agreement',
          content: response.data.content || '',
          version: response.data.version || '1.0'
        });
      }
    } catch (error) {
      console.error('Error fetching agreement:', error);
    }
  };

  const fetchAgreementList = async () => {
    try {
      const response = await axios.get(`${API}/business-partners/agreement/list`, {
        headers: getAuthHeader()
      });
      setAgreementList(response.data || []);
    } catch (error) {
      console.error('Error fetching agreement list:', error);
    }
  };

  const fetchAgreementAcceptances = async () => {
    try {
      const response = await axios.get(`${API}/business-partners/agreement/acceptances`, {
        headers: getAuthHeader()
      });
      setAgreementAcceptances(response.data || []);
    } catch (error) {
      console.error('Error fetching acceptances:', error);
    }
  };

  const handleSaveAgreement = async () => {
    setSavingAgreement(true);
    try {
      await axios.post(`${API}/business-partners/agreement`, agreement, {
        headers: getAuthHeader()
      });
      toast.success('Agreement saved successfully');
      await Promise.all([fetchAgreement(), fetchAgreementList(), fetchAgreementAcceptances()]);
    } catch (error) {
      console.error('Error saving agreement:', error);
      toast.error('Failed to save agreement');
    } finally {
      setSavingAgreement(false);
    }
  };

  const fetchPartners = async () => {
    const response = await axios.get(`${API}/business-partners`, {
      headers: getAuthHeader(),
      params: {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined
      }
    });
    setPartners(response.data);
  };

  const fetchStats = async () => {
    const response = await axios.get(`${API}/business-partners/stats`, {
      headers: getAuthHeader()
    });
    setStats(response.data);
  };

  const fetchDocumentConfigs = async () => {
    const response = await axios.get(`${API}/business-partners/config/documents`, {
      headers: getAuthHeader()
    });
    setDocumentConfigs(response.data);
  };

  const fetchCommissionConfigs = async () => {
    const response = await axios.get(`${API}/business-partners/config/commissions`, {
      headers: getAuthHeader()
    });
    setCommissionConfigs(response.data);
  };

  const fetchCommissionRecords = async () => {
    const response = await axios.get(`${API}/business-partners/commissions`, {
      headers: getAuthHeader(),
      params: {
        status: commissionFilter !== 'all' ? commissionFilter : undefined
      }
    });
    setCommissions(response.data);
  };

  const fetchStageVisibility = async () => {
    try {
      const response = await axios.get(`${API}/business-partners/config/stage-visibility`, {
        headers: getAuthHeader()
      });
      setStageVisibility(response.data);
    } catch {
      // Initialize with default stages if none exist
      const defaultStages = [
        { stage_key: 'new', stage_label: 'New', is_visible: true, display_order: 1, show_to_partner_as: 'Submitted' },
        { stage_key: 'qualified', stage_label: 'Qualified', is_visible: true, display_order: 2, show_to_partner_as: 'Under Review' },
        { stage_key: 'proposal_sent', stage_label: 'Proposal Sent', is_visible: true, display_order: 3, show_to_partner_as: 'Proposal Stage' },
        { stage_key: 'agreement_signed', stage_label: 'Agreement Signed', is_visible: true, display_order: 4, show_to_partner_as: 'Processing' },
        { stage_key: 'delivered', stage_label: 'Delivered', is_visible: true, display_order: 5, show_to_partner_as: 'Completed' },
        { stage_key: 'cancelled', stage_label: 'Cancelled', is_visible: true, display_order: 6, show_to_partner_as: 'Cancelled' },
      ];
      setStageVisibility(defaultStages);
    }
  };

  const fetchSettings = async () => {
    const response = await axios.get(`${API}/business-partners/config/settings`, {
      headers: getAuthHeader()
    });
    setBpSettings(response.data);
  };

  const handleApprovePartner = async (partnerId) => {
    try {
      await axios.post(`${API}/business-partners/${partnerId}/approve`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Partner approved successfully');
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve partner');
    }
  };

  const handleRejectPartner = async (partnerId, reason) => {
    try {
      await axios.post(`${API}/business-partners/${partnerId}/reject`, null, {
        headers: getAuthHeader(),
        params: { reason }
      });
      toast.success('Partner rejected');
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject partner');
    }
  };

  const handleSuspendPartner = async (partnerId) => {
    try {
      await axios.put(`${API}/business-partners/${partnerId}`, 
        { status: 'suspended' },
        { headers: getAuthHeader() }
      );
      toast.success('Partner suspended');
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend partner');
    }
  };

  const handleSaveDocConfig = async (config) => {
    try {
      if (editingDocConfig?.id) {
        await axios.put(`${API}/business-partners/config/documents/${editingDocConfig.id}`, config, {
          headers: getAuthHeader()
        });
        toast.success('Document config updated');
      } else {
        await axios.post(`${API}/business-partners/config/documents`, config, {
          headers: getAuthHeader()
        });
        toast.success('Document config created');
      }
      setShowDocConfigModal(false);
      setEditingDocConfig(null);
      fetchDocumentConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save config');
    }
  };

  const handleDeleteDocConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this document requirement?')) return;
    try {
      await axios.delete(`${API}/business-partners/config/documents/${configId}`, {
        headers: getAuthHeader()
      });
      toast.success('Document config deleted');
      fetchDocumentConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete config');
    }
  };

  const handleSaveCommConfig = async (config) => {
    try {
      if (editingCommConfig?.id) {
        await axios.put(`${API}/business-partners/config/commissions/${editingCommConfig.id}`, config, {
          headers: getAuthHeader()
        });
        toast.success('Commission config updated');
      } else {
        await axios.post(`${API}/business-partners/config/commissions`, config, {
          headers: getAuthHeader()
        });
        toast.success('Commission config created');
      }
      setShowCommConfigModal(false);
      setEditingCommConfig(null);
      fetchCommissionConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save config');
    }
  };

  const handleSaveStageVisibility = async () => {
    try {
      await axios.post(`${API}/business-partners/config/stage-visibility`, stageVisibility, {
        headers: getAuthHeader()
      });
      toast.success('Stage visibility updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/business-partners/config/settings`, bpSettings, {
        headers: getAuthHeader()
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleApproveCommission = async (commissionId) => {
    try {
      await axios.post(`${API}/business-partners/commissions/${commissionId}/approve`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Commission approved');
      fetchCommissionRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  // State for payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    reference: '',
    screenshot: null
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const openPaymentModal = (commission) => {
    setSelectedCommission(commission);
    setPaymentFormData({ reference: '', screenshot: null });
    setShowPaymentModal(true);
  };

  const handleMarkCommissionPaid = async (e) => {
    e.preventDefault();
    if (!paymentFormData.reference) {
      toast.error('Payment reference is required');
      return;
    }
    
    setSubmittingPayment(true);
    try {
      const formData = new FormData();
      formData.append('payment_reference', paymentFormData.reference);
      if (paymentFormData.screenshot) {
        formData.append('payment_screenshot', paymentFormData.screenshot);
      }
      
      await axios.post(
        `${API}/business-partners/commissions/${selectedCommission.id}/mark-paid`, 
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Commission marked as paid');
      setShowPaymentModal(false);
      fetchCommissionRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      under_review: { color: 'bg-blue-100 text-blue-800', icon: Eye },
      approved: { color: 'bg-green-100 text-green-800', icon: Check },
      rejected: { color: 'bg-red-100 text-red-800', icon: X },
      suspended: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: X },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon size={12} />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const tabs = [
    { id: 'partners', label: 'Partners', icon: Users },
    { id: 'documents', label: 'BP Documents', icon: FileText },
    { id: 'agreement', label: 'Partner Agreement', icon: FileSignature },
    { id: 'commissions', label: 'Commission Config', icon: IndianRupee },
    { id: 'commission-records', label: 'Commission Records', icon: TrendingUp },
    { id: 'stages', label: 'Stage Visibility', icon: Eye },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Business Partner Management</h1>
        <p className="text-gray-600">Manage partners, commissions, and configurations</p>
      </div>

      {/* Stats Cards */}
      {stats && activeTab === 'partners' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Partners</p>
                <p className="text-2xl font-bold">{stats.partners?.total || 0}</p>
              </div>
              <Users className="text-blue-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.partners?.pending || 0}</p>
              </div>
              <Clock className="text-yellow-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{stats.partners?.approved || 0}</p>
              </div>
              <Check className="text-green-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Commission</p>
                <p className="text-2xl font-bold">₹{(stats.commissions?.total_commission || 0).toLocaleString()}</p>
              </div>
              <IndianRupee className="text-purple-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Payout</p>
                <p className="text-2xl font-bold text-orange-600">₹{(stats.commissions?.pending_payout || 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="text-orange-500" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="flex overflow-x-auto border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-blue-600" size={24} />
            </div>
          ) : (
            <>
              {/* Partners Tab */}
              {activeTab === 'partners' && (
                <div>
                  <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        placeholder="Search partners..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && fetchPartners()}
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setTimeout(fetchPartners, 100); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="under_review">Under Review</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={fetchPartners} variant="outline">
                      <RefreshCw size={16} className="mr-2" /> Refresh
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {partners.map((partner) => (
                          <tr key={partner.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900">{partner.company_name}</p>
                                <p className="text-sm text-gray-500">{partner.business_type || 'N/A'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm text-gray-900">{partner.contact_person}</p>
                                <p className="text-xs text-gray-500">{partner.email}</p>
                                <p className="text-xs text-gray-500">{partner.phone}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">{getStatusBadge(partner.status)}</td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm">{partner.total_leads_submitted || 0} submitted</p>
                                <p className="text-xs text-green-600">{partner.total_leads_converted || 0} converted</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm font-medium">₹{(partner.total_commission_earned || 0).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">₹{(partner.total_commission_paid || 0).toLocaleString()} paid</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical size={16} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => { setSelectedPartner(partner); setShowPartnerModal(true); }}>
                                    <Eye size={14} className="mr-2" /> View Details
                                  </DropdownMenuItem>
                                  {(partner.status === 'pending' || partner.status === 'under_review') && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleApprovePartner(partner.id)} className="text-green-600">
                                        <Check size={14} className="mr-2" /> Approve
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => {
                                        const reason = window.prompt('Rejection reason:');
                                        if (reason) handleRejectPartner(partner.id, reason);
                                      }} className="text-red-600">
                                        <X size={14} className="mr-2" /> Reject
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {partner.status === 'approved' && (
                                    <DropdownMenuItem onClick={() => {
                                      if (window.confirm('Are you sure you want to suspend this partner?')) {
                                        handleSuspendPartner(partner.id);
                                      }
                                    }} className="text-orange-600">
                                      <AlertCircle size={14} className="mr-2" /> Suspend
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {partners.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <Users size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No partners found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Config Tab */}
              {activeTab === 'documents' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-gray-600">Configure documents required during partner onboarding</p>
                    <Button onClick={() => { setEditingDocConfig(null); setShowDocConfigModal(true); }}>
                      <Plus size={16} className="mr-2" /> Add Document
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {documentConfigs.map((config) => (
                      <div key={config.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <FileText size={18} className="text-blue-600" />
                            <span className="font-medium">{config.document_name}</span>
                            {config.is_required && <Badge className="bg-red-100 text-red-800">Required</Badge>}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{config.description || 'No description'}</p>
                          <p className="text-xs text-gray-400">Formats: {config.allowed_formats?.join(', ')} | Max: {config.max_size_mb}MB</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingDocConfig(config); setShowDocConfigModal(true); }}>
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteDocConfig(config.id)}>
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {documentConfigs.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <FileText size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No document requirements configured</p>
                        <p className="text-sm">Add documents that partners need to submit during onboarding</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Commission Config Tab */}
              {activeTab === 'commissions' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-gray-600">Configure commission rates for partners</p>
                    <Button onClick={() => { setEditingCommConfig(null); setShowCommConfigModal(true); }}>
                      <Plus size={16} className="mr-2" /> Add Rule
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {commissionConfigs.map((config) => (
                      <div key={config.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <IndianRupee size={18} className="text-green-600" />
                            <span className="font-medium">
                              {config.service_name || 'All Services (Default)'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {config.commission_type === 'percentage' 
                              ? `${config.commission_value}% of service value`
                              : `₹${config.commission_value} flat rate`
                            }
                          </p>
                          {(config.min_amount || config.max_amount) && (
                            <p className="text-xs text-gray-400">
                              {config.min_amount && `Min: ₹${config.min_amount}`}
                              {config.min_amount && config.max_amount && ' | '}
                              {config.max_amount && `Max: ₹${config.max_amount}`}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingCommConfig(config); setShowCommConfigModal(true); }}>
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                    {commissionConfigs.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <IndianRupee size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No commission rules configured</p>
                        <p className="text-sm">Add commission rules for partner payouts</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Commission Records Tab */}
              {activeTab === 'commission-records' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <Select value={commissionFilter} onValueChange={(val) => { setCommissionFilter(val); setTimeout(fetchCommissionRecords, 100); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={fetchCommissionRecords} variant="outline">
                      <RefreshCw size={16} className="mr-2" /> Refresh
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {commissions.map((comm) => (
                          <tr key={comm.id}>
                            <td className="px-4 py-3">
                              <p className="font-medium">{comm.partner_name}</p>
                              <p className="text-xs text-gray-500">{comm.partner_contact}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p>{comm.lead_name}</p>
                              <p className="text-xs text-gray-500">{comm.service_type}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-green-600">₹{comm.commission_amount?.toLocaleString()}</p>
                              <p className="text-xs text-gray-500">
                                {comm.commission_type === 'percentage' ? `${comm.commission_rate}%` : 'Flat'} on ₹{comm.base_amount?.toLocaleString()}
                              </p>
                            </td>
                            <td className="px-4 py-3">{getStatusBadge(comm.status)}</td>
                            <td className="px-4 py-3">
                              {comm.status === 'pending' && (
                                <Button size="sm" onClick={() => handleApproveCommission(comm.id)}>
                                  Approve
                                </Button>
                              )}
                              {comm.status === 'approved' && (
                                <Button size="sm" variant="outline" onClick={() => openPaymentModal(comm)}>
                                  Mark Paid
                                </Button>
                              )}
                              {comm.status === 'paid' && (
                                <div className="text-sm">
                                  <p className="text-gray-500">Ref: {comm.payment_reference}</p>
                                  {comm.payment_screenshot && (
                                    <a 
                                      href={`${BACKEND_URL}${comm.payment_screenshot}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                                    >
                                      <Eye size={12} /> View Screenshot
                                    </a>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {commissions.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <TrendingUp size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No commission records found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stage Visibility Tab */}
              {activeTab === 'stages' && (
                <div>
                  <p className="text-gray-600 mb-4">Configure which lead stages are visible to partners</p>
                  <div className="space-y-3">
                    {stageVisibility.map((stage, idx) => (
                      <div key={stage.stage_key} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={stage.is_visible}
                          onChange={(e) => {
                            const updated = [...stageVisibility];
                            updated[idx].is_visible = e.target.checked;
                            setStageVisibility(updated);
                          }}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{stage.stage_label}</p>
                          <p className="text-xs text-gray-500">Internal: {stage.stage_key}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Show as:</span>
                          <Input
                            value={stage.show_to_partner_as || ''}
                            onChange={(e) => {
                              const updated = [...stageVisibility];
                              updated[idx].show_to_partner_as = e.target.value;
                              setStageVisibility(updated);
                            }}
                            className="w-40"
                            placeholder="Partner label"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={handleSaveStageVisibility}>Save Changes</Button>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && bpSettings && (
                <div className="max-w-2xl space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payout Cycle</label>
                    <Select value={bpSettings.payout_cycle} onValueChange={(val) => setBpSettings({...bpSettings, payout_cycle: val})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="on_demand">On Demand</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Payout Amount (₹)</label>
                    <Input
                      type="number"
                      value={bpSettings.min_payout_amount || 500}
                      onChange={(e) => setBpSettings({...bpSettings, min_payout_amount: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Services Access</label>
                    <Select value={bpSettings.default_services_access} onValueChange={(val) => setBpSettings({...bpSettings, default_services_access: val})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Services</SelectItem>
                        <SelectItem value="none">No Services (Manual Assignment)</SelectItem>
                        <SelectItem value="selected">Selected Services Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={bpSettings.auto_approve_partners || false}
                      onChange={(e) => setBpSettings({...bpSettings, auto_approve_partners: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Auto-approve new partner registrations</label>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleSaveSettings}>Save Settings</Button>
                  </div>
                </div>
              )}

              {/* Partner Agreement Tab */}
              {activeTab === 'agreement' && (
                <div className="space-y-6">
                  {/* Agreement Version History */}
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-lg font-semibold mb-4">Agreement Versions ({agreementList.length})</h3>
                    {agreementList.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No agreements created yet. Create one below.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3 font-medium">ID</th>
                              <th className="text-left p-3 font-medium">Title</th>
                              <th className="text-left p-3 font-medium">Version</th>
                              <th className="text-left p-3 font-medium">Status</th>
                              <th className="text-left p-3 font-medium">Acceptances</th>
                              <th className="text-left p-3 font-medium">Created By</th>
                              <th className="text-left p-3 font-medium">Created At (IST)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {agreementList.map((agr) => (
                              <tr key={agr.id} className={`border-b hover:bg-gray-50 ${agr.is_active ? 'bg-green-50' : ''}`}>
                                <td className="p-3 font-mono text-xs">{agr.id}</td>
                                <td className="p-3">{agr.title}</td>
                                <td className="p-3">
                                  <Badge variant="outline">{agr.version}</Badge>
                                </td>
                                <td className="p-3">
                                  {agr.is_active ? (
                                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                                  ) : (
                                    <Badge variant="secondary">Inactive</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  <span className="font-medium">{agr.acceptance_count || 0}</span>
                                </td>
                                <td className="p-3">{agr.created_by || 'N/A'}</td>
                                <td className="p-3 text-xs">
                                  {agr.created_at ? new Date(agr.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Agreement Editor */}
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      {agreementList.length > 0 ? 'Create New Agreement Version' : 'Create Agreement'}
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Agreement Title</label>
                          <Input
                            value={agreement.title}
                            onChange={(e) => setAgreement({...agreement, title: e.target.value})}
                            placeholder="Business Partner Agreement"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                          <Input
                            value={agreement.version}
                            onChange={(e) => setAgreement({...agreement, version: e.target.value})}
                            placeholder="1.0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Agreement Content (HTML supported)</label>
                        <textarea
                          value={agreement.content}
                          onChange={(e) => setAgreement({...agreement, content: e.target.value})}
                          rows={15}
                          className="w-full p-3 border rounded-lg font-mono text-sm"
                          placeholder="Enter the full agreement content here. HTML tags are supported for formatting."
                        />
                      </div>
                      <div className="flex justify-between items-center pt-4 border-t">
                        <p className="text-sm text-gray-500">
                          Note: Updating the agreement will create a new version. Partners who accepted previous versions will need to accept the new one.
                        </p>
                        <Button onClick={handleSaveAgreement} disabled={savingAgreement}>
                          <Save size={16} className="mr-2" />
                          {savingAgreement ? 'Saving...' : 'Save Agreement'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  {agreement.content && (
                    <div className="bg-white rounded-lg border p-6">
                      <h3 className="text-lg font-semibold mb-4">Preview</h3>
                      <div className="border rounded-lg p-6 max-h-96 overflow-y-auto bg-gray-50">
                        <h2 className="text-xl font-bold mb-4">{agreement.title}</h2>
                        <div 
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: agreement.content }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Acceptance Records */}
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-lg font-semibold mb-4">Acceptance Records ({agreementAcceptances.length})</h3>
                    {agreementAcceptances.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No partners have accepted the agreement yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3 font-medium">Company</th>
                              <th className="text-left p-3 font-medium">Contact Person</th>
                              <th className="text-left p-3 font-medium">Email</th>
                              <th className="text-left p-3 font-medium">Version</th>
                              <th className="text-left p-3 font-medium">Accepted At</th>
                              <th className="text-left p-3 font-medium">IP Address</th>
                            </tr>
                          </thead>
                          <tbody>
                            {agreementAcceptances.map((acc) => (
                              <tr key={acc.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">{acc.company_name || 'N/A'}</td>
                                <td className="p-3">{acc.contact_person || 'N/A'}</td>
                                <td className="p-3">{acc.email || 'N/A'}</td>
                                <td className="p-3">
                                  <Badge variant="outline">{acc.agreement_version}</Badge>
                                </td>
                                <td className="p-3">
                                  {acc.accepted_at ? new Date(acc.accepted_at).toLocaleString() : 'N/A'}
                                </td>
                                <td className="p-3 font-mono text-xs">{acc.ip_address || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Document Config Modal */}
      <Dialog open={showDocConfigModal} onOpenChange={setShowDocConfigModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDocConfig ? 'Edit Document Requirement' : 'Add Document Requirement'}</DialogTitle>
          </DialogHeader>
          <DocumentConfigForm
            initialData={editingDocConfig}
            onSave={handleSaveDocConfig}
            onCancel={() => setShowDocConfigModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Commission Config Modal */}
      <Dialog open={showCommConfigModal} onOpenChange={setShowCommConfigModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCommConfig ? 'Edit Commission Rule' : 'Add Commission Rule'}</DialogTitle>
          </DialogHeader>
          <CommissionConfigForm
            initialData={editingCommConfig}
            onSave={handleSaveCommConfig}
            onCancel={() => setShowCommConfigModal(false)}
            servicesMaster={servicesMaster}
          />
        </DialogContent>
      </Dialog>

      {/* Partner Detail Modal */}
      <Dialog open={showPartnerModal} onOpenChange={setShowPartnerModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Details</DialogTitle>
          </DialogHeader>
          {selectedPartner && (
            <PartnerDetailView 
              partner={selectedPartner} 
              onClose={() => setShowPartnerModal(false)}
              onApprove={(partnerId) => {
                handleApprovePartner(partnerId);
                setShowPartnerModal(false);
              }}
              onReject={(partnerId, reason) => {
                handleRejectPartner(partnerId, reason);
                setShowPartnerModal(false);
              }}
              servicesMaster={servicesMaster}
              onUpdateServices={() => {
                fetchPartners();
                // Refresh selected partner data
                axios.get(`${API}/business-partners/${selectedPartner.id}`, { headers: getAuthHeader() })
                  .then(res => setSelectedPartner(res.data))
                  .catch(err => console.error('Error refreshing partner:', err));
              }}
              onRefresh={() => {
                // Refresh selected partner data after document review
                axios.get(`${API}/business-partners/${selectedPartner.id}`, { headers: getAuthHeader() })
                  .then(res => setSelectedPartner(res.data))
                  .catch(err => console.error('Error refreshing partner:', err));
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Commission Paid Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Commission as Paid</DialogTitle>
          </DialogHeader>
          {selectedCommission && (
            <form onSubmit={handleMarkCommissionPaid} className="space-y-4 pt-2">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Partner:</span>
                  <span className="font-medium">{selectedCommission.partner_name}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Lead:</span>
                  <span className="font-medium">{selectedCommission.lead_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Commission Amount:</span>
                  <span className="font-bold text-green-600">₹{selectedCommission.commission_amount?.toLocaleString()}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Reference *</label>
                <input
                  type="text"
                  value={paymentFormData.reference}
                  onChange={(e) => setPaymentFormData({...paymentFormData, reference: e.target.value})}
                  placeholder="Transaction ID / UTR Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Screenshot (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPaymentFormData({...paymentFormData, screenshot: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                {paymentFormData.screenshot && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {paymentFormData.screenshot.name}
                  </p>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submittingPayment} className="flex-1">
                  {submittingPayment ? 'Processing...' : 'Mark as Paid'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Document Config Form Component
const DocumentConfigForm = ({ initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    document_name: initialData?.document_name || '',
    document_key: initialData?.document_key || '',
    description: initialData?.description || '',
    is_required: initialData?.is_required ?? true,
    allowed_formats: initialData?.allowed_formats || ['pdf', 'jpg', 'jpeg', 'png'],
    max_size_mb: initialData?.max_size_mb || 5,
    display_order: initialData?.display_order || 0,
  });

  const handleSubmit = () => {
    if (!formData.document_name || !formData.document_key) {
      toast.error('Name and Key are required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-4 pt-2">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Document Name</label>
        <input
          type="text"
          value={formData.document_name}
          onChange={(e) => setFormData({...formData, document_name: e.target.value})}
          placeholder="e.g., Business Registration"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Document Key</label>
        <input
          type="text"
          value={formData.document_key}
          onChange={(e) => setFormData({...formData, document_key: e.target.value.toLowerCase().replace(/\s/g, '_')})}
          placeholder="e.g., business_registration"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, no spaces)</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Brief description of the document"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <input
          type="checkbox"
          checked={formData.is_required}
          onChange={(e) => setFormData({...formData, is_required: e.target.checked})}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <label className="text-sm text-gray-700">Required document for partner onboarding</label>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Max File Size (MB)</label>
        <input
          type="number"
          value={formData.max_size_mb}
          onChange={(e) => setFormData({...formData, max_size_mb: parseInt(e.target.value)})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min={1}
          max={50}
        />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit}>Save</Button>
      </div>
    </div>
  );
};

// Commission Config Form Component
const CommissionConfigForm = ({ initialData, onSave, onCancel, servicesMaster }) => {
  const [formData, setFormData] = useState({
    service_id: initialData?.service_id || '',
    service_name: initialData?.service_name || '',
    services: initialData?.services || [],
    commission_type: initialData?.commission_type || 'percentage',
    commission_value: initialData?.commission_value || 10,
    min_amount: initialData?.min_amount || '',
    max_amount: initialData?.max_amount || '',
  });

  const [showServiceDropdown, setShowServiceDropdown] = useState(false);

  const toggleServiceSelection = (serviceTitle) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceTitle)
        ? prev.services.filter(s => s !== serviceTitle)
        : [...prev.services, serviceTitle]
    }));
  };

  const handleSubmit = () => {
    onSave({
      ...formData,
      // For backward compatibility, set service_name from first selected service
      service_name: formData.services.length === 1 ? formData.services[0] : (formData.services.length > 1 ? 'Multiple' : ''),
      min_amount: formData.min_amount ? parseFloat(formData.min_amount) : null,
      max_amount: formData.max_amount ? parseFloat(formData.max_amount) : null,
    });
  };

  return (
    <div className="space-y-4 pt-2">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Services (leave empty for default rule)
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowServiceDropdown(!showServiceDropdown)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between"
          >
            <span className={formData.services.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
              {formData.services.length === 0 
                ? 'All Services (Default)' 
                : formData.services.length === 1 
                  ? formData.services[0]
                  : `${formData.services.length} services selected`}
            </span>
            <ChevronRight size={16} className={`transform transition-transform ${showServiceDropdown ? 'rotate-90' : ''}`} />
          </button>
          
          {showServiceDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {servicesMaster && servicesMaster.length > 0 ? (
                servicesMaster.map((service) => (
                  <label
                    key={service.id || service.title}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service.title)}
                      onChange={() => toggleServiceSelection(service.title)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300"
                    />
                    <span className="text-sm">{service.title}</span>
                  </label>
                ))
              ) : (
                <p className="px-3 py-2 text-sm text-gray-400">No services available</p>
              )}
            </div>
          )}
        </div>
        {formData.services.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.services.map(service => (
              <span key={service} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                {service}
                <button type="button" onClick={() => toggleServiceSelection(service)} className="hover:text-blue-600">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Commission Type</label>
        <select 
          value={formData.commission_type} 
          onChange={(e) => setFormData({...formData, commission_type: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
          <option value="percentage">Percentage (%)</option>
          <option value="flat">Flat Rate (₹)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Commission Value {formData.commission_type === 'percentage' ? '(%)' : '(₹)'}
        </label>
        <input
          type="number"
          value={formData.commission_value}
          onChange={(e) => setFormData({...formData, commission_value: parseFloat(e.target.value)})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Commission Limits (Optional)</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Amount (₹)</label>
            <input
              type="number"
              value={formData.min_amount}
              onChange={(e) => setFormData({...formData, min_amount: e.target.value})}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Amount (₹)</label>
            <input
              type="number"
              value={formData.max_amount}
              onChange={(e) => setFormData({...formData, max_amount: e.target.value})}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit}>Save</Button>
      </div>
    </div>
  );
};

// Partner Detail View Component
const PartnerDetailView = ({ partner, onClose, onApprove, onReject, servicesMaster, onUpdateServices, onRefresh }) => {
  const { getAuthHeader } = useAuth();
  // Filter out 'all' from the initial services - we'll handle it separately
  const initialServices = (partner.approved_services || []).filter(s => s !== 'all');
  const [selectedServices, setSelectedServices] = useState(initialServices);
  const [allServicesEnabled, setAllServicesEnabled] = useState((partner.approved_services || []).includes('all'));
  const [savingServices, setSavingServices] = useState(false);
  
  // Reset Password state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Leads Modal state
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [partnerLeads, setPartnerLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  
  const fetchPartnerLeads = async () => {
    setLoadingLeads(true);
    try {
      const response = await axios.get(
        `${API}/leads?partner_id=${partner.id}&source=BPL`,
        { headers: getAuthHeader() }
      );
      setPartnerLeads(response.data.leads || response.data || []);
      setShowLeadsModal(true);
    } catch (error) {
      console.error('Error fetching partner leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoadingLeads(false);
    }
  };
  
  const getDocumentUrl = (filePath, partnerId) => {
    if (!filePath) return '#';
    // Extract filename from the path
    const filename = filePath.split('/').pop();
    // Use the API endpoint which handles authentication and proper file serving
    return `${API}/business-partners/documents/${partnerId}/${filename}`;
  };

  const toggleService = (serviceTitle) => {
    if (allServicesEnabled) return; // Don't allow individual selection when "all" is enabled
    setSelectedServices(prev => 
      prev.includes(serviceTitle) 
        ? prev.filter(s => s !== serviceTitle)
        : [...prev, serviceTitle]
    );
  };

  const toggleAllServices = () => {
    setAllServicesEnabled(prev => !prev);
    if (!allServicesEnabled) {
      // When enabling "all", clear individual selections
      setSelectedServices([]);
    }
  };

  const handleSaveServices = async () => {
    setSavingServices(true);
    try {
      // Build the services array - include 'all' if enabled, otherwise individual services
      const servicesToSave = allServicesEnabled ? ['all'] : selectedServices;
      
      await axios.put(`${API}/business-partners/${partner.id}/services`, 
        { approved_services: servicesToSave },
        { headers: getAuthHeader() }
      );
      toast.success('Partner services updated');
      if (onUpdateServices) onUpdateServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update services');
    } finally {
      setSavingServices(false);
    }
  };

  // Check if there are changes - compare current state with original
  const originalHasAll = (partner.approved_services || []).includes('all');
  const originalServices = (partner.approved_services || []).filter(s => s !== 'all').sort();
  const currentServices = selectedServices.sort();
  const hasServiceChanges = 
    allServicesEnabled !== originalHasAll || 
    JSON.stringify(currentServices) !== JSON.stringify(originalServices);

  const handleViewDocument = async (doc) => {
    try {
      const filename = doc.file_path?.split('/').pop();
      if (!filename) {
        toast.error('Document path not found');
        return;
      }
      
      const response = await axios.get(
        `${API}/business-partners/documents/${partner.id}/${filename}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob',
          validateStatus: (status) => status < 500 // Don't throw for 4xx errors
        }
      );
      
      // Check if response is an error (will be JSON)
      if (response.status >= 400) {
        // Try to read error message from blob
        const text = await response.data.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || 'Failed to load document');
        } catch {
          toast.error('Failed to load document');
        }
        return;
      }
      
      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      // Handle blob response error properly
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || 'Failed to load document');
        } catch {
          toast.error('Failed to load document');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load document');
      }
    }
  };

  const handleReviewDocument = async (doc, status) => {
    try {
      let rejectionReason = null;
      if (status === 'rejected') {
        rejectionReason = window.prompt('Please provide a reason for rejection:');
        if (rejectionReason === null) return; // User cancelled
      }
      
      await axios.post(
        `${API}/business-partners/${partner.id}/documents/${doc.id}/review`,
        null,
        {
          headers: getAuthHeader(),
          params: { 
            status,
            rejection_reason: rejectionReason
          }
        }
      );
      
      toast.success(`Document ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      
      // Refresh partner data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error reviewing document:', error);
      toast.error(error.response?.data?.detail || 'Failed to review document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/business-partners/${partner.id}/documents/${docId}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Document deleted successfully');
      
      // Refresh partner data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/business-partners/${partner.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const canApproveOrReject = partner.status === 'pending' || partner.status === 'under_review';

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {canApproveOrReject && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-yellow-600" />
            <span className="font-medium text-yellow-800">
              Partner is awaiting approval ({partner.status === 'pending' ? 'Documents Pending' : 'Under Review'})
            </span>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              onClick={() => onApprove && onApprove(partner.id)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check size={14} className="mr-1" /> Approve
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                const reason = window.prompt('Rejection reason:');
                if (reason && onReject) onReject(partner.id, reason);
              }}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <X size={14} className="mr-1" /> Reject
            </Button>
          </div>
        </div>
      )}

      {/* Partner Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Company Name</p>
          <p className="font-semibold text-gray-900">{partner.company_name}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Contact Person</p>
          <p className="font-semibold text-gray-900">{partner.contact_person}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Email</p>
          <p className="font-semibold text-gray-900">{partner.email}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</p>
          <p className="font-semibold text-gray-900">{partner.phone}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Business Type</p>
          <p className="font-semibold text-gray-900">{partner.business_type || 'N/A'}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">GST Number</p>
          <p className="font-semibold text-gray-900">{partner.gst_number || 'N/A'}</p>
        </div>
      </div>

      {/* Address - Full Width */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Address</p>
        <p className="font-semibold text-gray-900">
          {partner.address ? `${partner.address}, ${partner.city}, ${partner.state} - ${partner.pincode}` : 'N/A'}
        </p>
      </div>

      {/* Bank Details Section */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Building2 size={18} className="text-green-600" />
            Bank Details
          </h3>
        </div>
        {partner.bank_details ? (
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account Holder Name</p>
              <p className="font-semibold text-gray-900">{partner.bank_details.account_holder_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Bank Name</p>
              <p className="font-semibold text-gray-900">{partner.bank_details.bank_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account Number</p>
              <p className="font-semibold text-gray-900">{partner.bank_details.account_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">IFSC Code</p>
              <p className="font-semibold text-gray-900">{partner.bank_details.ifsc_code || 'N/A'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Branch</p>
              <p className="font-semibold text-gray-900">{partner.bank_details.branch || 'N/A'}</p>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <Building2 className="mx-auto mb-2 opacity-50" size={32} />
            <p>No bank details provided</p>
          </div>
        )}
      </div>

      {/* Documents Section */}
      {partner.documents && partner.documents.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              Uploaded Documents
            </h3>
          </div>
          <div className="divide-y">
            {partner.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{doc.document_name || doc.document_type}</p>
                    <p className="text-xs text-gray-500">{doc.file_name || 'Document'}</p>
                    {doc.rejection_reason && (
                      <p className="text-xs text-red-500 mt-1">Reason: {doc.rejection_reason}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={
                    doc.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    doc.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {doc.status}
                  </Badge>
                  
                  {/* Document Actions */}
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleViewDocument(doc)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                      data-testid={`view-doc-${doc.id}`}
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <a 
                      href={getDocumentUrl(doc.file_path, partner.id)} 
                      download
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800 hover:underline font-medium"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <Download size={14} />
                      Download
                    </a>
                    
                    {/* Approve/Reject buttons - only show for pending documents */}
                    {doc.status === 'pending' && (
                      <>
                        <button 
                          onClick={() => handleReviewDocument(doc, 'approved')}
                          className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800 font-medium px-2 py-1 rounded hover:bg-green-50"
                          data-testid={`approve-doc-${doc.id}`}
                        >
                          <Check size={14} />
                          Approve
                        </button>
                        <button 
                          onClick={() => handleReviewDocument(doc, 'rejected')}
                          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded hover:bg-red-50"
                          data-testid={`reject-doc-${doc.id}`}
                        >
                          <X size={14} />
                          Reject
                        </button>
                      </>
                    )}
                    
                    {/* Delete button - admin can delete any document */}
                    <button 
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Services Section */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Check size={18} className="text-green-600" />
            Approved Services
          </h3>
          {hasServiceChanges && (
            <Button 
              size="sm" 
              onClick={handleSaveServices}
              disabled={savingServices}
            >
              {savingServices ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-500 mb-3">Select services this partner can pitch/sell:</p>
          
          {/* All Services Toggle */}
          <label 
            className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors mb-3 ${
              allServicesEnabled 
                ? 'bg-green-50 border-green-300' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="checkbox"
              checked={allServicesEnabled}
              onChange={toggleAllServices}
              className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">All Services (Grant access to all current and future services)</span>
          </label>

          {/* Individual Services */}
          <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${allServicesEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {servicesMaster && servicesMaster.length > 0 ? (
              servicesMaster.map((service) => (
                <label 
                  key={service.id || service.title} 
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                    allServicesEnabled || selectedServices.includes(service.title) 
                      ? 'bg-blue-50 border-blue-300' 
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={allServicesEnabled || selectedServices.includes(service.title)}
                    onChange={() => toggleService(service.title)}
                    disabled={allServicesEnabled}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{service.title}</span>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-400 col-span-3">No services available</p>
            )}
          </div>
          
          {/* Status indicator */}
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-gray-500">
              {allServicesEnabled ? (
                <span className="text-green-600 font-medium">✓ Access to all {servicesMaster?.length || 0} services</span>
              ) : selectedServices.length > 0 ? (
                <><span className="font-medium">{selectedServices.length}</span> of {servicesMaster?.length || 0} service(s) selected</>
              ) : (
                <span className="text-orange-600">No services selected - partner cannot submit leads</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
        <div 
          className="bg-blue-50 p-4 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={fetchPartnerLeads}
          data-testid="leads-submitted-count"
        >
          <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Leads Submitted</p>
          <p className="text-2xl font-bold text-blue-700">{partner.total_leads_submitted || 0}</p>
          <p className="text-xs text-blue-500 mt-1">Click to view</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Leads Converted</p>
          <p className="text-2xl font-bold text-green-700">{partner.total_leads_converted || 0}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg text-center">
          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Commission Earned</p>
          <p className="text-2xl font-bold text-purple-700">₹{(partner.total_commission_earned || 0).toLocaleString()}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg text-center">
          <p className="text-xs text-orange-600 uppercase tracking-wide mb-1">Commission Paid</p>
          <p className="text-2xl font-bold text-orange-700">₹{(partner.total_commission_paid || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Admin Actions */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Admin Actions</h3>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowResetPasswordModal(true)}
            data-testid="reset-password-btn"
          >
            <Settings size={14} className="mr-1" />
            Reset Password
          </Button>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Reset Password</h3>
                <button 
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Reset password for {partner.company_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  className="flex-1"
                  data-testid="submit-reset-password-btn"
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Leads Modal */}
      {showLeadsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Leads from {partner.company_name}</h3>
                <p className="text-sm text-gray-500">{partnerLeads.length} leads submitted</p>
              </div>
              <button 
                onClick={() => setShowLeadsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {loadingLeads ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-blue-600" size={32} />
                </div>
              ) : partnerLeads.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 font-medium text-gray-600">Name</th>
                        <th className="text-left p-3 font-medium text-gray-600">Email</th>
                        <th className="text-left p-3 font-medium text-gray-600">Phone</th>
                        <th className="text-left p-3 font-medium text-gray-600">Service</th>
                        <th className="text-left p-3 font-medium text-gray-600">Stage</th>
                        <th className="text-left p-3 font-medium text-gray-600">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {partnerLeads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-medium text-gray-900">{lead.name}</p>
                          </td>
                          <td className="p-3 text-gray-600">{lead.email || 'N/A'}</td>
                          <td className="p-3 text-gray-600">{lead.phone || 'N/A'}</td>
                          <td className="p-3 text-gray-600">{lead.service_type || lead.visa_type || 'N/A'}</td>
                          <td className="p-3">
                            <Badge className={
                              lead.stage === 'won' ? 'bg-green-100 text-green-800' :
                              lead.stage === 'lost' || lead.stage === 'cancelled' ? 'bg-red-100 text-red-800' :
                              lead.stage === 'new' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {lead.stage || 'New'}
                            </Badge>
                          </td>
                          <td className="p-3 text-gray-500 text-sm">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="mx-auto mb-3 opacity-50" size={48} />
                  <p className="font-medium">No leads found</p>
                  <p className="text-sm">This partner hasn't submitted any leads yet</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setShowLeadsModal(false)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPartnerManager;
